import { useMemo } from 'react';
import { FileEdit, AlertCircle, Check, Clock, RotateCcw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getStatusActionLabels, useConsignmentActions } from '@/features/consignment-actions';
import type { ConsignmentStatusAction, ConsignmentSummary } from '@/entities/consignments';
import type { SelectionAction } from '@/shared/ui';

type SelectableConsignment = Pick<ConsignmentSummary, 'id' | 'allowedActions' | 'resetTargetStatusCode'>;

interface UseConsignmentSelectionProps {
  selectedIds: string[];
  consignments?: SelectableConsignment[];
  onClearSelection?: () => void;
}

const STATUS_ACTIONS: ConsignmentStatusAction[] = ['ACCEPT', 'SET_ON_HOLD', 'CANCEL', 'RESET'];

const STATUS_ACTION_ICONS: Record<ConsignmentStatusAction, React.ReactNode> = {
  ACCEPT: <Check className="h-4 w-4" />,
  SET_ON_HOLD: <Clock className="h-4 w-4" />,
  CANCEL: <XCircle className="h-4 w-4" />,
  RESET: <RotateCcw className="h-4 w-4" />
};

/**
 * Bulk actions for the selected consignments. A status action sends only the selected rows whose `allowedActions`
 * carry it and is disabled when none do — rows outside the loaded page have no known actions, so they never go out.
 */
export function useConsignmentSelection({ selectedIds, consignments, onClearSelection }: UseConsignmentSelectionProps) {
  const { t } = useTranslation();
  const { actions, dialogs, isLoading } = useConsignmentActions({
    selectedIds,
    onClearSelection
  });

  const selectedRows = useMemo(
    () => (consignments ?? []).filter((consignment) => selectedIds.includes(consignment.id)),
    [consignments, selectedIds]
  );

  const selectionActions: SelectionAction[] = useMemo(() => {
    const statusLabels = getStatusActionLabels(t);

    return [
      {
        id: 'edit',
        label: t('listaction.RQ.EDT'),
        icon: <FileEdit className="h-4 w-4" />,
        onClick: () => actions.editView(selectedIds),
        intent: 'action'
      },
      {
        id: 'priority',
        label: t('listaction.RQ.PRT'),
        icon: <AlertCircle className="h-4 w-4" />,
        onClick: () => actions.changePriority(selectedIds),
        intent: 'action'
      },
      ...STATUS_ACTIONS.map((action): SelectionAction => {
        const rows = selectedRows.filter((row) => row.allowedActions?.includes(action));

        return {
          id: action.toLowerCase().replace(/_/g, '-'),
          label: statusLabels[action],
          icon: STATUS_ACTION_ICONS[action],
          onClick: () => actions.changeStatus(action, rows),
          intent: action === 'CANCEL' ? 'danger' : 'action',
          disabled: rows.length === 0
        };
      })
    ];
  }, [actions, selectedIds, selectedRows, t]);

  return {
    selectedCount: selectedIds.length,
    selectionActions,
    dialogs,
    isSubmitting: isLoading,
    isVisible: selectedIds.length > 0
  };
}
